
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AdminSubmission } from './useAdminData';

export interface LeadWithStatus extends AdminSubmission {
  lead_status: 'nieuw' | 'contact_opgenomen' | 'interesse' | 'offerte_verstuurd' | 'gesloten' | 'verloren';
  notes: string | null;
  next_action_date: string | null;
  assigned_to: string | null;
  lead_updated_at: string;
}

export const useLeadsData = () => {
  const [leads, setLeads] = useState<LeadWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchLeads = async () => {
    try {
      setLoading(true);
      
      // Fetch submissions with lead status
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('quickscan_submissions')
        .select(`
          *,
          lead_status (
            status,
            notes,
            next_action_date,
            assigned_to,
            updated_at
          )
        `)
        .order('created_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      // Fetch related data for each submission
      const enrichedLeads = await Promise.all(
        submissionsData.map(async (submission) => {
          const [
            { data: solutions },
            { data: peakHours },
            { data: expansionPlans },
            { data: priorities },
            { data: recommendations },
            { data: expansionDetails }
          ] = await Promise.all([
            supabase.from('quickscan_current_solutions').select('solution').eq('submission_id', submission.id),
            supabase.from('quickscan_energy_peak_hours').select('peak_hour').eq('submission_id', submission.id),
            supabase.from('quickscan_expansion_plans').select('plan_type').eq('submission_id', submission.id),
            supabase.from('quickscan_priorities').select('priority_key, priority_value').eq('submission_id', submission.id),
            supabase.from('quickscan_recommendations').select('recommendation').eq('submission_id', submission.id),
            supabase.from('quickscan_expansion_details').select('plan_type, details').eq('submission_id', submission.id)
          ]);

          const leadStatus = submission.lead_status?.[0] || { 
            status: 'nieuw', 
            notes: null, 
            next_action_date: null, 
            assigned_to: null,
            updated_at: submission.created_at
          };

          return {
            ...submission,
            current_solutions: solutions?.map(s => s.solution) || [],
            energy_peak_hours: peakHours?.map(p => p.peak_hour) || [],
            expansion_plans: expansionPlans?.map(p => p.plan_type) || [],
            priorities: priorities?.reduce((acc, p) => ({ ...acc, [p.priority_key]: p.priority_value }), {}) || {},
            recommendations: recommendations?.map(r => r.recommendation) || [],
            expansion_details: expansionDetails?.reduce((acc, d) => ({ ...acc, [d.plan_type]: d.details }), {}) || {},
            lead_status: leadStatus.status,
            notes: leadStatus.notes,
            next_action_date: leadStatus.next_action_date,
            assigned_to: leadStatus.assigned_to,
            lead_updated_at: leadStatus.updated_at
          } as LeadWithStatus;
        })
      );

      // Ensure intake_leads surfaced via CRM leads table are also visible
      const { data: crmLeads, error: leadsError } = await supabase
        .from('leads')
        .select('origin_type, origin_submission_id, stage, next_action_date, created_at, updated_at')
        .is('deleted_at', null)
        .in('origin_type', ['quickscan', 'quickscan_submission', 'intake_quickscan']);
      if (leadsError) throw leadsError;

      const originIds = Array.from(new Set(
        (crmLeads || [])
          .map(l => l.origin_submission_id)
          .filter((id): id is string => !!id)
      ));

      let stageBySubmissionId: Record<string, { stage: LeadWithStatus['lead_status']; next_action_date: string | null; updated_at: string } > = {};
      (crmLeads || []).forEach(l => {
        if (l.origin_submission_id) {
          stageBySubmissionId[l.origin_submission_id] = {
            stage: (l.stage as LeadWithStatus['lead_status']) || 'nieuw',
            next_action_date: l.next_action_date || null,
            updated_at: l.updated_at || l.created_at,
          };
        }
      });

      // Load quickscan submissions for these leads in one batch
      const { data: originSubs, error: originErr } = originIds.length > 0
        ? await supabase
            .from('quickscan_submissions')
            .select('*')
            .in('id', originIds)
        : { data: [], error: null } as any;
      if (originErr) throw originErr;

      const byId: Record<string, LeadWithStatus> = {};
      enrichedLeads.forEach(l => { byId[l.id] = l; });

      // Merge: override status with stage from CRM leads if present
      Object.entries(stageBySubmissionId).forEach(([submissionId, info]) => {
        const existing = byId[submissionId];
        if (existing) {
          byId[submissionId] = {
            ...existing,
            lead_status: info.stage,
            next_action_date: info.next_action_date,
            lead_updated_at: info.updated_at,
          };
        }
      });

      // Add any missing submissions referenced by CRM leads (edge case)
      (originSubs as any[]).forEach(sub => {
        if (!byId[sub.id]) {
          const info = stageBySubmissionId[sub.id] || { stage: 'nieuw', next_action_date: null, updated_at: sub.created_at };
          byId[sub.id] = {
            ...sub,
            current_solutions: [],
            energy_peak_hours: [],
            expansion_plans: [],
            priorities: {},
            recommendations: [],
            expansion_details: {},
            lead_status: info.stage,
            notes: null,
            next_action_date: info.next_action_date,
            assigned_to: null,
            lead_updated_at: info.updated_at,
          } as LeadWithStatus;
        }
      });

      setLeads(Object.values(byId));
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: 'Fout bij laden leads',
        description: 'Er ging iets mis bij het ophalen van de leads.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: 'nieuw' | 'contact_opgenomen' | 'interesse' | 'offerte_verstuurd' | 'gesloten' | 'verloren') => {
    try {
      const { error } = await supabase
        .from('lead_status')
        .upsert({
          submission_id: leadId,
          status: newStatus
        }, {
          onConflict: 'submission_id'
        });

      if (error) throw error;

      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { ...lead, lead_status: newStatus, lead_updated_at: new Date().toISOString() }
          : lead
      ));

      toast({
        title: 'Status bijgewerkt',
        description: 'Lead status is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast({
        title: 'Fout bij bijwerken status',
        description: 'Er ging iets mis bij het bijwerken van de lead status.',
        variant: 'destructive'
      });
    }
  };

  const addLeadNote = async (leadId: string, note: string) => {
    try {
      const { error } = await supabase
        .from('lead_status')
        .upsert({
          submission_id: leadId,
          notes: note
        }, {
          onConflict: 'submission_id'
        });

      if (error) throw error;

      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { ...lead, notes: note, lead_updated_at: new Date().toISOString() }
          : lead
      ));

      toast({
        title: 'Notitie toegevoegd',
        description: 'De notitie is succesvol toegevoegd.',
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Fout bij toevoegen notitie',
        description: 'Er ging iets mis bij het toevoegen van de notitie.',
        variant: 'destructive'
      });
    }
  };

  const updateLeadDetails = async (leadId: string, details: { next_action_date: string | null; assigned_to: string | null }) => {
    try {
      const { error } = await supabase
        .from('lead_status')
        .upsert({
          submission_id: leadId,
          next_action_date: details.next_action_date,
          assigned_to: details.assigned_to
        }, {
          onConflict: 'submission_id'
        });

      if (error) throw error;

      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { 
              ...lead, 
              next_action_date: details.next_action_date,
              assigned_to: details.assigned_to,
              lead_updated_at: new Date().toISOString() 
            }
          : lead
      ));

      toast({
        title: 'Details bijgewerkt',
        description: 'Lead details zijn succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating lead details:', error);
      toast({
        title: 'Fout bij bijwerken details',
        description: 'Er ging iets mis bij het bijwerken van de lead details.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  return {
    leads,
    loading,
    refetch: fetchLeads,
    updateLeadStatus,
    addLeadNote,
    updateLeadDetails
  };
};
